import React from "react"
import { useSelector, useDispatch } from "react-redux"

import {
    APPROVED,
    DELETE,
    EDIT,
    REVIEW,
    YES,
    WORKFLOW_RERUN,
    VIEW,
} from "const"
import { CcReviewReviewItem, CcReviewReviewHistory } from "server/models"
import { deepCopy, hasProjectAccess } from "client/utils"
import {
    PRM_CC_REVIEW,
    PRM_CC_DRAFTS,
    StoreType,
    DisplayColumns,
    setCcReviewReviewSelected,
    setCcReviewReviewActive,
    setCcReviewReviewPopupMenuShow,
    setCcReviewReviewPopupMenuTarget,
    showCcReviewReviewColumnSettings,
} from "client/store"
import {
    UserIcon,
    TableColumn,
    NotAssigned,
    SettingsButton,
    TableMenu,
    CheckButton,
    DateFormatter,
    DraftSaved,
    Empty,
} from "client/components"

import ReviewItemRevisionList from "./revisionList"
import InputSelectAll from "./selectAll"
import { OpenAnswer } from "../open"

const Columns = () => {
    const baseRoute = useSelector((state: StoreType) => state.ccReviewDocument.baseRoute)
    const activeFile = useSelector((state: StoreType) => state.ccReviewDocument.activeFile)
    const activeVersion = useSelector((state: StoreType) => state.ccReviewDocument.activeVersion)

    const revisionActiveList = useSelector((state: StoreType) => state.ccReviewReview.revisionActiveList)
    const itemStateList = useSelector((state: StoreType) => state.ccReviewReview.itemStateList)
    const itemHistoryList = useSelector((state: StoreType) => state.ccReviewReview.itemHistoryList)
    const tempAnswerList = useSelector((state: StoreType) => state.ccReviewReview.tempAnswerList)
    const selected = useSelector((state: StoreType) => state.ccReviewReview.selected)
    const typeList = useSelector((state: StoreType) => state.ccReviewReviewType.typeList)

    const responseList = useSelector((state: StoreType) => state.ccResponse.responseList)
    const levelList = useSelector((state: StoreType) => state.ccApprovalLevel.levelList)
    const moduleLevelList = useSelector((state: StoreType) => state.ccApprovalLevel.moduleLevelList)
    const levelIds = moduleLevelList[2] || [] // review level ids

    const userList = useSelector((state: StoreType) => state.user.userList)

    const reviewReviewColumns = useSelector((state: StoreType) => state.system.userSettings.displayColumns.reviewReview)

    const userData = useSelector((state: StoreType) => state.system.userData)
    const lang = useSelector((state: StoreType) => state.system.lang)
    const dispatch = useDispatch()


    const displayColumns: DisplayColumns = {
        remark: reviewReviewColumns.remark !== undefined ? reviewReviewColumns.remark : true,
        version: reviewReviewColumns.version !== undefined ? reviewReviewColumns.version : true,
        typeId: reviewReviewColumns.typeId !== undefined ? reviewReviewColumns.typeId : true,
        revision: reviewReviewColumns.revision !== undefined ? reviewReviewColumns.revision : true,
        creator: reviewReviewColumns.creator !== undefined ? reviewReviewColumns.creator : true,
        state: reviewReviewColumns.state !== undefined ? reviewReviewColumns.state : true,
        createdAt: reviewReviewColumns.createdAt !== undefined ? reviewReviewColumns.createdAt : true,
    }
    levelIds.forEach((levelId) => {
        const columnId = `level_${levelId}`
        displayColumns[columnId] = reviewReviewColumns[columnId] !== undefined ? reviewReviewColumns[columnId] : true
        const columnAnswerId = `${columnId}_answer`
        displayColumns[columnAnswerId] = reviewReviewColumns[columnAnswerId] !== undefined ? reviewReviewColumns[columnAnswerId] : true
    })

    const editAccess = hasProjectAccess(PRM_CC_REVIEW, EDIT)
    const deleteAccess = hasProjectAccess(PRM_CC_REVIEW, DELETE)
    const viewDraftsAccess = hasProjectAccess(PRM_CC_DRAFTS, VIEW)

    const selectItem = (itemId: number) => {
        const checked = selected.includes(itemId)
        let newSelected: number[] = deepCopy(selected)
        if (checked) {
            newSelected = newSelected.filter((id) => id !== itemId)
        } else {
            newSelected.push(itemId)
        }
        dispatch(setCcReviewReviewSelected(newSelected))
    }

    const showContextMenu = (
        e: React.MouseEvent,
        active: CcReviewReviewItem,
    ) => {
        e.preventDefault()
        e.stopPropagation()

        dispatch(setCcReviewReviewPopupMenuTarget(e.currentTarget as HTMLElement))
        dispatch(setCcReviewReviewPopupMenuShow(true))
        dispatch(setCcReviewReviewActive(active))
    }

    const showColumnSettings = () => {
        dispatch(showCcReviewReviewColumnSettings(true))
    }

    const onClick = (item: CcReviewReviewItem) => {
        const activeRevision = revisionActiveList[item.id]
        if (!activeRevision) return
        OpenAnswer(baseRoute, REVIEW, activeFile.id, activeVersion.id, item.id, activeRevision.id, dispatch)
    }

    const columns: TableColumn[] = [
        {
            field: "setting",
            headerFormatter: () => <SettingsButton onClick={showColumnSettings} />,
            cellMinWidth: "46px",
            cellMaxWidth: "46px",
            formatter: (item: CcReviewReviewItem) => <TableMenu onClick={(e) => showContextMenu(e, item)} />,
        },
        {
            field: "remark",
            header: lang.reviewItem,
            display: displayColumns.remark,
            sortable: true,
            cellMinWidth: "200px",
            cellMaxWidth: "350px",
            onClick: (item: CcReviewReviewItem) => onClick(item),
        },
        {
            field: "version",
            header: lang.revision,
            display: displayColumns.version,
            sortable: true,
            cellMinWidth: "150px",
            cellMaxWidth: "250px",
            onClick: (item: CcReviewReviewItem) => onClick(item),
            formatter: (item: CcReviewReviewItem) => item.version.revision,
        },
        {
            field: "typeId",
            header: lang.kind,
            display: displayColumns.typeId,
            sortable: true,
            cellMinWidth: "150px",
            onClick: (item: CcReviewReviewItem) => onClick(item),
            formatter: (item: CcReviewReviewItem) => {
                const type = typeList[item.typeId]
                return type && type.name
            },
        },
        {
            field: "revision",
            header: lang.revisionList,
            display: displayColumns.revision,
            cellMinWidth: "200px",
            overflowVisible: true,
            formatter: (item: CcReviewReviewItem) => <ReviewItemRevisionList item={item} />,
        },
        {
            field: "creator",
            header: lang.createdBy,
            display: displayColumns.creator,
            cellMinWidth: "150px",
            onClick: (item: CcReviewReviewItem) => onClick(item),
            formatter: (item: CcReviewReviewItem) => {
                const user = userList[item.creator]
                return <UserIcon user={user} size="sm" />
            },
        },
    ]

    // add columns for each level
    levelIds.forEach((levelId) => {
        const columnId = `level_${levelId}`
        columns.push({
            field: columnId,
            header: levelList[levelId].name,
            display: displayColumns[columnId],
            cellMinWidth: "150px",
            onClick: (item: CcReviewReviewItem) => onClick(item),
            formatter: (item: CcReviewReviewItem) => {
                const itemState = itemStateList[item.id] || null
                const settings = itemState && itemState.settings
                    ? JSON.parse(itemState.settings)
                    : {}
                const userId = settings[levelId] || null
                const user = userList[userId]
                return user ? <UserIcon user={user} size="sm" /> : <NotAssigned />
            },
        })

        const columnAnswerId = `${columnId}_answer`
        columns.push({
            field: columnAnswerId,
            header: `${levelList[levelId].name} ${lang.answer}`,
            display: displayColumns[columnAnswerId],
            cellMinWidth: "150px",
            onClick: (item: CcReviewReviewItem) => onClick(item),
            formatter: (item: CcReviewReviewItem) => {
                const itemHistory = itemHistoryList[item.id] || []
                const itemState = itemStateList[item.id] || null

                let levelHistory: CcReviewReviewHistory = null
                for (let i = itemHistory.length - 1; i >= 0; i--) {
                    const history = itemHistory[i]
                    if (history.workflow === WORKFLOW_RERUN) {
                        break
                    }
                    if (!history.systemMessage && history.prevLevelId === levelId) {
                        levelHistory = history
                        break
                    }
                    if (!history.systemMessage && history.parentId === null) { // take answer only from the last branch
                        break
                    }
                }

                if (
                    itemState
                    && itemState.levelId === levelId
                    && (itemState.verifierId === userData.userId || viewDraftsAccess)
                    && tempAnswerList[item.id]
                    && tempAnswerList[item.id][itemState.verifierId]
                ) {
                    return <DraftSaved />
                }
                if (levelHistory) {
                    const response = responseList[levelHistory.responseId] || null
                    return response ? response.response : <Empty />
                }
                return <Empty />
            },
        })
    })

    columns.push(
        {
            field: "state",
            header: lang.state,
            display: displayColumns.state,
            cellMinWidth: "200px",
            style: {
                fontSize: "13px",
            },
            onClick: (item: CcReviewReviewItem) => onClick(item),
            formatter: (item: CcReviewReviewItem) => {
                const itemHistory = itemHistoryList[item.id] || []
                let lastHistory: CcReviewReviewHistory = null

                // get the last history row made by user, not by 'system'
                for (let i = itemHistory.length - 1; i >= 0; i--) {
                    const history = itemHistory[i]
                    if (history.workflow === WORKFLOW_RERUN) {
                        break
                    }
                    if (!history.systemMessage) {
                        lastHistory = history
                        break
                    }
                }

                const itemState = itemStateList[item.id] || null
                const level = itemState ? levelList[itemState.levelId] : null
                const verifier = itemState ? userList[itemState.verifierId] : null

                if (lastHistory && lastHistory.nextStatus === APPROVED) {
                    const response = responseList[lastHistory.responseId]
                    return `${lang.status}: ${response && response.approved === YES ? lang.approved : lang.notApproved}`
                }

                if (level) {
                    const firstLevel = level.id === levelIds[0]
                    return (
                        <div>
                            <div>
                                {`${level.name}: `}
                                {verifier ? <UserIcon user={verifier} size="sm" /> : <NotAssigned />}
                            </div>
                            <div>
                                {`${lang.status}: ${firstLevel ? lang.open : lang.verification}`}
                            </div>
                        </div>
                    )
                }

                if (!level && lastHistory) { // approval level was removed or not set
                    return (
                        <div>
                            {`${lang.status}: ${lang.verification}`}
                        </div>
                    )
                }

                if (!level && !lastHistory) { // no any data
                    return (
                        <div>
                            {`${lang.status}: ${lang.open}`}
                        </div>
                    )
                }

                return null
            },
        },
        {
            field: "createdAt",
            header: lang.created,
            display: displayColumns.createdAt,
            sortable: true,
            onClick: (item: CcReviewReviewItem) => onClick(item),
            formatter: (item: CcReviewReviewItem) => <DateFormatter date={item.createdAt} />,
        },
        {
            field: "delete",
            centerAlign: true,
            deleteAccess: (item: CcReviewReviewItem) => deleteAccess || item.creator === userData.userId,
        },
    )

    if (editAccess) {
        columns.unshift({
            field: "select",
            headerFormatter: () => <InputSelectAll />,
            cellWidth: "46px",
            cellMinWidth: "46px",
            formatter: (item: CcReviewReviewItem) => (
                <CheckButton
                    checked={selected.includes(item.id)}
                    padding={false}
                    margin={false}
                    onChange={() => selectItem(item.id)}
                />
            ),
        })
    }

    return { columns, displayColumns }
}
export default Columns
